import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { garantirUsuario } from '@/lib/usuario';
import { abrirPeloLink, ehContaDeEquipe } from '@/lib/equipeLink';
import { solicitarAvisosAposSucesso } from '@/lib/avisosSucesso';
import { useModoEquipe } from '@/components/feature/ModoEquipe';
import FundoEvento from '@/components/feature/FundoEvento';
import Logo from '@/components/ui/Logo';
import Cartao from '@/components/ui/Cartao';
import Botao from '@/components/ui/Botao';
import Dica from '@/components/feature/Dica';
import { dataCurtaBrasilia, funcTexto } from '@/pages/gerenciar/painel/equipe-dia/utils';

interface Convite {
  ok?: boolean;
  situacao: string;
  evento_id: number | null;
  evento: string;
  quando: string | null;
  quem_chamou: string | null;
  nome: string | null;
  funcoes: unknown;
  caixa: boolean;
  portaria: boolean;
  barracas: string[];
  sou_eu: boolean;
}

function normalizar(v: unknown): Convite | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  return {
    ok: o.ok as boolean | undefined,
    situacao: String(o.situacao ?? 'nao_encontrado'),
    evento_id: o.evento_id == null ? null : Number(o.evento_id),
    evento: String(o.evento ?? ''),
    quando: o.quando == null ? null : String(o.quando),
    quem_chamou: o.quem_chamou == null ? null : String(o.quem_chamou),
    nome: o.nome == null ? null : String(o.nome),
    funcoes: o.funcoes,
    caixa: o.caixa === true,
    portaria: o.portaria === true,
    barracas: Array.isArray(o.barracas) ? (o.barracas as string[]) : [],
    sou_eu: o.sou_eu === true,
  };
}

function Moldura({ children }: { children: ReactNode }) {
  return (
    <FundoEvento>
      <div className="flex min-h-screen flex-col">
        <header className="flex items-center justify-center px-4 pb-2 pt-8">
          <Logo imgClassName="h-11 w-auto object-contain" />
        </header>
        <main className="flex flex-1 items-start justify-center px-4 pb-16 pt-4">
          <div className="w-full max-w-[440px]">{children}</div>
        </main>
      </div>
    </FundoEvento>
  );
}

function TelaMensagem({ texto, voltar }: { texto: string; voltar?: boolean }) {
  return (
    <Moldura>
      <h1 className="text-center text-[24px] font-extrabold text-white">Convite da equipe</h1>
      <Cartao variante="vidro" className="mt-4 p-6 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/70 text-primary-600">
          <i className="ri-mail-open-line text-2xl" />
        </span>
        <p className="mt-4 text-[16px] text-foreground-800">{texto}</p>
        {voltar && (
          <Link
            to="/"
            className="mt-5 flex h-[52px] w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl bg-primary-500 text-[16px] font-bold text-white transition-colors hover:bg-primary-600"
          >
            Voltar ao início
          </Link>
        )}
      </Cartao>
    </Moldura>
  );
}

// A tela de convite "de hoje" (203). No modo pessoal ela aparece com um recado no topo
// e um botão para trocar a conta deste celular pela conta de equipe.
function TelaConvite({
  token,
  mensagem,
  mostrarBotaoCelular,
  onAbrirNesteCelular,
}: {
  token: string;
  mensagem?: string;
  mostrarBotaoCelular?: boolean;
  onAbrirNesteCelular?: () => void;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [carregando, setCarregando] = useState(true);
  const [convite, setConvite] = useState<Convite | null>(null);
  const [logado, setLogado] = useState(false);
  const [aceitando, setAceitando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;

    (async () => {
      setCarregando(true);
      try {
        if (ativo) setLogado(!!user);

        const { data, error } = await supabase.rpc('equipe_convite', { p_token: token });
        if (error) throw error;
        if (!ativo) return;
        setConvite(normalizar(data));
      } catch {
        if (ativo) setErro('Não foi possível carregar o convite. Tente de novo.');
      } finally {
        if (ativo) setCarregando(false);
      }
    })();

    return () => {
      ativo = false;
    };
  }, [token]);

  // Função aceita: pede os avisos do evento (1,5 s) na tela de sucesso.
  useEffect(() => {
    if (convite && convite.situacao === 'aceito' && convite.sou_eu && convite.evento) {
      solicitarAvisosAposSucesso(convite.evento);
    }
  }, [convite]);

  async function recarregar() {
    const { data } = await supabase.rpc('equipe_convite', { p_token: token });
    setConvite(normalizar(data));
  }

  function entrar() {
    navigate(`/entrar?voltar=${encodeURIComponent(`/equipe/${token}`)}`);
  }

  async function aceitar() {
    setAceitando(true);
    setErro(null);
    try {
      await garantirUsuario();
      const { data, error } = await supabase.rpc('equipe_aceitar_convite', { p_token: token });
      if (error) throw error;
      const resp = data as { ok?: boolean; erro?: string } | null;
      if (!resp || resp.ok === false) {
        setErro(resp?.erro || 'Não foi possível aceitar o convite.');
        return;
      }
      await recarregar();
    } catch {
      setErro('Não foi possível aceitar agora. Tente de novo.');
    } finally {
      setAceitando(false);
    }
  }

  if (carregando) {
    return (
      <Moldura>
        <div className="h-64 animate-pulse rounded-3xl bg-white/40 backdrop-blur-sm" />
      </Moldura>
    );
  }

  if (erro && !convite) {
    return <TelaMensagem texto={erro} voltar />;
  }

  if (!convite || convite.ok === false || convite.situacao === 'nao_encontrado') {
    return <TelaMensagem texto="Convite não encontrado. Confira o link com a direção." voltar />;
  }

  if (convite.situacao === 'cancelado') {
    return <TelaMensagem texto="Este convite foi cancelado. Peça um novo à direção." voltar />;
  }

  if (convite.situacao === 'vencido') {
    return <TelaMensagem texto="Este convite venceu. Peça um novo à direção." voltar />;
  }

  if (convite.situacao === 'aceito' && !convite.sou_eu) {
    return <TelaMensagem texto="Este convite já foi usado. Peça um novo à direção." voltar />;
  }

  const funcoesTxt = funcTexto(convite.funcoes);
  const barracas = convite.barracas;

  // Passo 3: já aceito (e é a pessoa dona do convite).
  if (convite.situacao === 'aceito' && convite.sou_eu) {
    return (
      <Moldura>
        <h1 className="text-center text-[24px] font-extrabold text-white">Tudo certo!</h1>
        <Cartao variante="vidro" className="mt-4 p-6">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-100 text-3xl">
            🎉
          </span>
          <p className="mt-4 text-center text-[18px] font-bold text-foreground-950">
            Pronto{convite.nome ? `, ${convite.nome.split(' ')[0]}` : ''}! Você está na equipe do{' '}
            {convite.evento}.
          </p>

          <div className="mt-5 space-y-2">
            {convite.caixa && (
              <Link
                to="/caixa"
                className="flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 text-[16px] font-bold text-white transition-colors hover:bg-primary-600"
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-money-dollar-circle-line" />
                </span>
                Abrir o caixa
              </Link>
            )}
            {barracas.length > 0 && (
              <Link
                to="/loja/barraca"
                className="flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-accent-500 text-[16px] font-bold text-white transition-colors hover:bg-accent-600"
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-store-2-line" />
                </span>
                Abrir minha barraca
              </Link>
            )}
            {convite.portaria && convite.evento_id && (
              <Link
                to={`/portaria/${convite.evento_id}`}
                className="flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-secondary-500 text-[16px] font-bold text-white transition-colors hover:bg-secondary-600"
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-door-open-line" />
                </span>
                Abrir a portaria
              </Link>
            )}
          </div>

          <p className="mt-4 text-center text-[13px] text-foreground-500">
            No dia, é só abrir o Se Liga e tocar no menu.
          </p>

          {mostrarBotaoCelular && (
            <button
              type="button"
              onClick={onAbrirNesteCelular}
              className="mt-3 flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl border border-primary-300 bg-white text-[15px] font-bold text-primary-700 transition-colors hover:bg-primary-50"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-smartphone-line" />
              </span>
              Abrir a função neste celular
            </button>
          )}
        </Cartao>
      </Moldura>
    );
  }

  // Passo 1/2: convite aguardando.
  const explicao: string[] = [];
  if (convite.caixa) explicao.push('vender as fichas no caixa');
  if (barracas.length > 0) explicao.push('entregar as fichas na barraca');
  if (convite.portaria) explicao.push('ler os ingressos na entrada');

  return (
    <Moldura>
      <div className="flex items-center justify-center gap-2">
        <h1 className="text-[24px] font-extrabold text-white">Convite da equipe</h1>
        <Dica id="equipe.convite" claro />
      </div>

      {mensagem && (
        <div className="mt-4 rounded-2xl border border-warning-300 bg-warning-50 px-4 py-3">
          <p className="text-[13px] font-semibold text-warning-800">{mensagem}</p>
        </div>
      )}

      <Cartao variante="vidro" className="mt-4 p-6">
        <p className="text-center text-[15px] text-foreground-600">
          {convite.quem_chamou ? `${convite.quem_chamou} te chamou para servir no` : 'Você foi chamado para servir no'}
        </p>
        <p className="mt-1 text-center text-[20px] font-extrabold leading-tight text-foreground-950">
          {convite.evento}
        </p>
        {convite.quando && (
          <p className="mt-1 text-center text-[14px] text-foreground-500">
            {dataCurtaBrasilia(convite.quando) || convite.quando}
          </p>
        )}

        <div className="mt-4 rounded-2xl border border-warning-200 bg-warning-50 px-4 py-3">
          <p className="text-[13px] font-semibold text-warning-800">Suas funções</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {convite.caixa && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-[13px] font-semibold text-foreground-800">
                💳 Caixa
              </span>
            )}
            {barracas.map((b) => (
              <span
                key={b}
                className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-[13px] font-semibold text-foreground-800"
              >
                🍹 {b}
              </span>
            ))}
            {convite.portaria && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-[13px] font-semibold text-foreground-800">
                🎟 Portaria
              </span>
            )}
            {!convite.caixa && barracas.length === 0 && !convite.portaria && (
              <span className="text-[13px] text-foreground-500">{funcoesTxt || 'Equipe'}</span>
            )}
          </div>
          {explicao.length > 0 && (
            <p className="mt-2 text-[13px] text-warning-800/90">
              Você vai {explicao.join(' e ')}.
            </p>
          )}
        </div>

        {erro && (
          <p className="mt-3 flex items-center gap-1.5 text-[14px] text-danger-600" role="alert">
            <span className="flex h-4 w-4 shrink-0 items-center justify-center">
              <i className="ri-error-warning-line" />
            </span>
            {erro}
          </p>
        )}

        <div className="mt-5">
          <Botao
            tamanho="lg"
            cheio
            icone="ri-login-circle-line"
            carregando={aceitando}
            onClick={logado ? aceitar : entrar}
          >
            Entrar na minha conta e abrir
          </Botao>
        </div>
        <p className="mt-3 text-center text-[13px] text-foreground-500">
          Você entra com a sua conta Se Liga e aceita o convite.
        </p>

        {mostrarBotaoCelular && (
          <button
            type="button"
            onClick={onAbrirNesteCelular}
            className="mt-3 flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl border border-primary-300 bg-white text-[15px] font-bold text-primary-700 transition-colors hover:bg-primary-50"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-smartphone-line" />
            </span>
            Abrir a função neste celular
          </button>
        )}
      </Cartao>
    </Moldura>
  );
}

type Fluxo = 'verificando' | 'pessoal' | 'erro' | 'aceito';

// Ao abrir /equipe/<token>, a tela tenta abrir a função sozinha (modo equipe).
// Se não der (conta pessoal neste celular, ou erro de rede/sessão), mostra a tela "de hoje".
export default function EquipeConvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { recarregar } = useModoEquipe();

  const [fluxo, setFluxo] = useState<Fluxo>('verificando');
  const [mensagemPessoal, setMensagemPessoal] = useState<string | undefined>(undefined);
  const [erroLink, setErroLink] = useState<{ mensagem: string; motivo?: string } | null>(null);
  const jaRodou = useRef(false);

  const abrir = useCallback(
    async (usarEsteCelular = false) => {
      setFluxo('verificando');

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user ?? null;

      // Não logado: a tela de convite convida a entrar com a conta da pessoa.
      if (!user) {
        setFluxo('pessoal');
        setMensagemPessoal('Entre com a sua conta Se Liga para abrir a sua função.');
        return;
      }

      // Conta de equipe (celular de função antigo): mantém o fluxo de sempre.
      if (ehContaDeEquipe(user)) {
        const r = await abrirPeloLink(token ?? '', { usarEsteCelular });
        if (r.tipo === 'abrir') {
          await recarregar();
          navigate('/servir', { replace: true });
          return;
        }
        if (r.tipo === 'pessoal') {
          setMensagemPessoal(r.mensagem);
          setFluxo('pessoal');
          return;
        }
        setErroLink({ mensagem: r.mensagem, motivo: r.motivo });
        setFluxo('erro');
        return;
      }

      // Conta pessoal: aceita o convite direto com a conta dela (sem trocar o login).
      const { data: a, error } = await supabase.rpc('equipe_aceitar_convite', {
        p_token: token ?? '',
      });
      if (error) {
        setErroLink({
          mensagem: 'Sem sinal agora. Toque de novo no link em alguns segundos.',
          motivo: 'rede',
        });
        setFluxo('erro');
        return;
      }
      const resp = a as { ok?: boolean; erro?: string } | null;
      if (!resp?.ok) {
        const msg = resp?.erro || 'Não foi possível abrir a sua função.';
        if (/já foi aceito/i.test(msg)) {
          setErroLink({
            mensagem:
              'Este link ainda está no modo antigo. Peça à direção para atualizar (script 231).',
            motivo: 'antigo',
          });
        } else {
          setErroLink({ mensagem: msg, motivo: 'aceite' });
        }
        setFluxo('erro');
        return;
      }

      setFluxo('aceito');
    },
    [token, recarregar, navigate],
  );

  useEffect(() => {
    if (jaRodou.current) return; // StrictMode: chama só uma vez por abertura
    jaRodou.current = true;
    void abrir(false);
  }, [abrir]);

  if (fluxo === 'verificando') {
    return (
      <Moldura>
        <div className="flex flex-col items-center justify-center gap-3 py-24">
          <i className="ri-loader-4-line animate-spin text-4xl text-white" />
          <p className="text-[16px] font-semibold text-white">Abrindo a sua tela…</p>
        </div>
      </Moldura>
    );
  }

  if (fluxo === 'erro' && erroLink) {
    return (
      <Moldura>
        <h1 className="text-center text-[24px] font-extrabold text-white">Convite da equipe</h1>
        <Cartao variante="vidro" className="mt-4 p-6 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-danger-100 text-danger-600">
            <i className="ri-emotion-sad-line text-2xl" />
          </span>
          <p className="mt-4 text-[16px] text-foreground-800">{erroLink.mensagem}</p>
          <button
            type="button"
            onClick={() => void abrir(false)}
            className="mt-5 flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 text-[16px] font-bold text-white transition-colors hover:bg-primary-600"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-refresh-line" />
            </span>
            Tentar de novo
          </button>
        </Cartao>
      </Moldura>
    );
  }

  return (
    <TelaConvite
      token={token ?? ''}
      mensagem={fluxo === 'pessoal' ? mensagemPessoal : undefined}
      mostrarBotaoCelular={false}
      onAbrirNesteCelular={() => void abrir(true)}
    />
  );
}